import { UpdateFlowRule } from '@/types/hydraulic'

/**
 * Flow rule constants, taken from the controller documentation rather than
 * guessed: AquaLogic 500 & GO Controller UI v230 §5.9.2–5.9.4 for the
 * behaviour, Interface and Controller API v204 §3.4.8 for the enums and the
 * sample values.
 */

/**
 * Seeded when a rule is created from the pipe list, so adding a rule is one
 * click and the user lands on the sensors and valves — the part they actually
 * came to configure.
 *
 * These are the platform's agreed defaults, mirrored from the backend so a
 * rule behaves the same whichever door it was born through:
 * QuickSetupService.SetupFlowRuleAsync and the Excel-import fallback in
 * SetupAppService both create rules with exactly these values. The limits are
 * armed (−20% / +10%) but stay dormant until the rule has a sensor to measure
 * with, and autoAction is off, so nothing is ever stopped by a fresh rule.
 *
 * allowedIdleFlow is metric (l/m); the rail converts for imperial users the
 * same way the backend import does.
 */
export const FLOW_RULE_DEFAULTS: Omit<
  UpdateFlowRule,
  'id' | 'interfaceId' | 'pipeId'
> = {
  active: true,
  autoAction: false,
  allowedIdleFlow: 10,
  maxLowDeviation: 20,
  maxHighDeviation: 10,
  startDelay: 20,
  changeDelay: 20,
  errorDelay: 120,
  stableDeviation: 10,
  stableCount: 5,
  stableMax: 5,
}

/**
 * Danger red for text. NOT the theme's `error.main`: this project maps
 * error.main to #fff2f2 (a background tint), so text coloured with it turns
 * white-on-white — the same class of trap as text.primary. #dc3545 is the
 * palette's RED.main, the red the config screens' Cancel buttons already use.
 */
export const DANGER_TEXT = '#dc3545'

export type StateTone = 'ok' | 'bad' | 'busy' | 'idle'

/**
 * GET /we/flowRule/stateEnum. The old form rendered `state === 0 ? 'OK' :
 * 'Unexpected flow'`, so six of the eight states were reported as the wrong
 * thing — including the two that are not faults at all.
 *
 * Everything below keys off the NUMBER the controller sends. Labels are Tolgee
 * keys, never values: a translated label must never be what a lookup maps back
 * from, or picking "Booster pump" in Korean would save something else.
 */
export const FLOW_RULE_STATES: Record<number, { labelKey: string; tone: StateTone }> = {
  0: { labelKey: 'flowRuleStateOk', tone: 'ok' },
  1: { labelKey: 'flowRuleStateNoFlow', tone: 'bad' },
  2: { labelKey: 'flowRuleStateUnexpectedFlow', tone: 'bad' },
  3: { labelKey: 'flowRuleStateFlowTooLow', tone: 'bad' },
  4: { labelKey: 'flowRuleStateFlowTooHigh', tone: 'bad' },
  5: { labelKey: 'flowRuleStateResourceMissing', tone: 'bad' },
  6: { labelKey: 'flowRuleStateVerifyFlow', tone: 'busy' },
  7: { labelKey: 'flowRuleStateLearnFlow', tone: 'busy' },
}

export const flowRuleState = (state?: number | null) =>
  FLOW_RULE_STATES[Number(state ?? 0)] ?? { labelKey: '', tone: 'idle' as StateTone }

/** GET /we/flowValve/stateEnum */
export const VALVE_STATES: Record<number, { labelKey: string; tone: StateTone }> = {
  0: { labelKey: 'commonWordUnknown', tone: 'idle' },
  1: { labelKey: 'flowValveStateClosed', tone: 'ok' },
  2: { labelKey: 'commonActionsOpen', tone: 'busy' },
}

/** GET /we/flowValve/errorStateEnum */
export const VALVE_ERRORS: Record<number, string> = {
  0: 'flowRulePanelNone',
  1: 'flowValveErrorIo',
  2: 'flowRuleStateNoFlow',
}

/**
 * GET /we/flowValve/typeEnum — the stored numbers, and the two groups they
 * split into for the UI.
 *
 * The split is presentation only ("Pump / Normally open / Normally closed /
 * Booster pump" is one list mixing two questions: what the hardware IS, and
 * how it behaves). The wire format is still the single `type` number, and that
 * number — not any label — is what the row carries and the save sends.
 */
export type ValveGroupId = 'valve' | 'pump'

export const VALVE_GROUP_VALVE: ValveGroupId = 'valve'
export const VALVE_GROUP_PUMP: ValveGroupId = 'pump'
export const VALVE_GROUP_IDS: ValveGroupId[] = [VALVE_GROUP_VALVE, VALVE_GROUP_PUMP]

/** Valve type numbers per group, in the order they are offered. */
export const VALVE_TYPES_BY_GROUP: Record<ValveGroupId, number[]> = {
  valve: [1, 2],
  pump: [0, 3],
}

/** The type a row falls back to when its group changes. */
export const defaultTypeForGroup = (group: ValveGroupId) =>
  VALVE_TYPES_BY_GROUP[group][0]

/** Which group a stored type number belongs to. */
export const valveGroupOf = (type?: number | null): ValveGroupId =>
  Number(type) === 1 || Number(type) === 2 ? VALVE_GROUP_VALVE : VALVE_GROUP_PUMP

export const VALVE_TYPE_LABEL_KEYS: Record<number, string> = {
  0: 'flowValveTypePump',
  1: 'flowValveTypeNormallyOpen',
  2: 'flowValveTypeNormallyClosed',
  3: 'flowValveTypeBoosterPump',
}

export const VALVE_GROUP_LABEL_KEYS: Record<ValveGroupId, string> = {
  valve: 'flowValveDeviceValve',
  pump: 'flowValveDevicePump',
}

/**
 * What each choice actually does, from the SRC manual (AquaLogic 500 & GO
 * Controller UI v230 §5.9.3.3 and the hydraulics overview).
 *
 * The manual is blunt about something the four-way list hides: "The pump, N/C
 * MV and booster pumps behave the same way, but just gives the user the option
 * to define what it is." Only Normally open behaves differently.
 */
export const VALVE_TYPE_TIP_KEYS: Record<number, string> = {
  0: 'flowValveTipPump',
  1: 'flowValveTipNormallyOpen',
  2: 'flowValveTipNormallyClosed',
  3: 'flowValveTipBoosterPump',
}

export const VALVE_GROUP_TIP_KEYS: Record<ValveGroupId, string> = {
  valve: 'flowValveTipDeviceValve',
  pump: 'flowValveTipDevicePump',
}

/** Writes are serialised: these endpoints 500 when they arrive concurrently. */
export const SAVE_RETRY_LIMIT = 3
export const SAVE_RETRY_DELAY_MS = 300
export const SAVE_REQUEST_GAP_MS = 120
